/**
 * #116 — Layers/Cycles solve filtering.
 *
 * The Cycles panel lets a user "ignore" a component (or a whole cycle group)
 * so it is excluded from the solve WITHOUT deleting it from the canvas. This is
 * purely a SOLVE-INPUT transformation: the canonical `store.nodes`/`store.edges`
 * are untouched (so the node still renders and round-trips through persistence);
 * only the array handed to the solver is pruned.
 *
 * Pruning rule: drop every ignored node, and any edge incident to an ignored
 * node (a dangling edge would otherwise reference a node the solver never sees).
 * When nothing is ignored the inputs pass through by identity, so the default /
 * untouched solve is byte-for-byte unchanged.
 */
import type { Node, Edge } from 'reactflow';

export interface SolveInput<N extends Node = Node> {
  nodes: N[];
  edges: Edge[];
}

/**
 * Return the nodes/edges the solver should actually see, given the set of
 * ignored node ids. Returns the SAME array references when `ignoredIds` is
 * empty (no allocation, no behaviour change on the default path).
 */
export function filterSolveInput<N extends Node>(
  nodes: N[],
  edges: Edge[],
  ignoredIds: ReadonlySet<string> | readonly string[],
): SolveInput<N> {
  const ignored = ignoredIds instanceof Set ? ignoredIds : new Set(ignoredIds);
  if (ignored.size === 0) {
    return { nodes, edges };
  }
  const keptNodes = nodes.filter((n) => !ignored.has(n.id));
  const keptEdges = edges.filter((e) => !ignored.has(e.source) && !ignored.has(e.target));
  return { nodes: keptNodes, edges: keptEdges };
}
